import React, { useEffect, useState, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { getCustomers, getUsage } from '../api';
import api from '../api';
import STARLIMS_LOGO from '../logo';

const REGION_COLORS = {'us-west-2':'#378ADD','eu-west-2':'#1D9E75','eu-west-3':'#D85A30','ap-southeast-2':'#7F77DD'};
const REGION_LABELS = {'us-west-2':'USW2','eu-west-2':'EUW2','eu-west-3':'EUW3','ap-southeast-2':'ASE2'};
const TYPE_COLORS   = {'P':'#378ADD','B':'#1D9E75','L':'#D85A30','Unknown':'#888780'};
const TYPE_LABELS   = {'P':'Portal','B':'Background','L':'Legacy','Unknown':'Unknown'};

function RegionTrendTable({ data }) {
  if (!data || data.length === 0) return (
    <div className="card">
      <div className="card-header"><div className="card-title">Cost by region — last 12 months</div></div>
      <div className="loading">No cost data</div>
    </div>
  );

  const fmt   = v => v > 0 ? `$${Number(v).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}` : '—';
  const fmtMo = m => new Date(m).toLocaleDateString('en-GB',{month:'long',year:'numeric'});

  // Sort by region asc, then month descending (newest first within each region)
  const sorted = [...data].sort((a, b) => {
    if (a.region < b.region) return -1;
    if (a.region > b.region) return 1;
    return b.month.localeCompare(a.month);
  });

  // Count rows per region for rowSpan
  const regionCounts = {};
  sorted.forEach(r => { regionCounts[r.region] = (regionCounts[r.region] || 0) + 1; });
  const seenRegions = new Set();

  return (
    <div className="card" style={{overflow:'hidden'}}>
      <div className="card-header"><div className="card-title">Cost by region — last 12 months</div></div>
      <div style={{overflowX:'auto'}}>
        <table>
          <thead>
            <tr>
              <th>Region</th>
              <th>Month</th>
              <th style={{textAlign:'right'}}>Gross</th>
              <th style={{textAlign:'right',color:'#378ADD'}}>Net</th>
              <th style={{textAlign:'right',color:'#1D9E75'}}>SP Saving</th>
              <th style={{textAlign:'right',color:'#7F77DD'}}>EDP</th>
              <th style={{textAlign:'right'}}>Saving %</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, i) => {
              const showRegion = !seenRegions.has(r.region);
              if (showRegion) seenRegions.add(r.region);
              const gross      = r.cost_gross || 0;
              const net        = r.cost_net   || 0;
              const savingPct  = gross > 0 ? ((gross - net) / gross * 100).toFixed(1) : '0.0';
              return (
                <tr key={`${r.region}|${r.month}`} style={showRegion && i > 0 ? {borderTop:'2px solid #e5e5e5'} : {}}>
                  {showRegion ? (
                    <td rowSpan={regionCounts[r.region]} style={{fontWeight:600,verticalAlign:'top',paddingTop:8,whiteSpace:'nowrap'}}>
                      {r.region}
                    </td>
                  ) : null}
                  <td style={{color:'#666',whiteSpace:'nowrap'}}>{fmtMo(r.month)}</td>
                  <td style={{textAlign:'right',color:'#666'}}>{fmt(gross)}</td>
                  <td style={{textAlign:'right',color:'#378ADD',fontWeight:500}}>{fmt(net)}</td>
                  <td style={{textAlign:'right',color:'#1D9E75'}}>{fmt(r.cost_sp_saving)}</td>
                  <td style={{textAlign:'right',color:'#7F77DD'}}>{fmt(r.cost_edp)}</td>
                  <td style={{textAlign:'right'}}>
                    <span className={`badge ${parseFloat(savingPct) > 0 ? 'badge-active' : 'badge-inactive'}`}>{savingPct}%</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function CustomerOverview({ dimension, activeOnly }) {
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected]   = useState('');
  const [usage, setUsage]         = useState(null);
  const [dbSizes, setDbSizes]     = useState(null);
  const [volumes, setVolumes]     = useState(null);
  const [sessions, setSessions]   = useState(null);
  const [cost, setCost]           = useState(null);
  const [regionTrend, setRegionTrend] = useState(null);
  const [custInfo, setCustInfo]   = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const printRef = useRef();
  const [allExportDim, setAllExportDim] = useState('last_year');

  useEffect(() => {
    getCustomers(activeOnly).then(r => {
      setCustomers(r.data);
      setSelected('');
      setUsage(null); setDbSizes(null); setVolumes(null); setSessions(null); setCost(null); setCustInfo(null); setRegionTrend(null);
    });
  }, [activeOnly]);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    setError(null);
    Promise.all([
      getUsage(selected, dimension),
      api.get(`/api/v1/dbsizes/${selected}?dimension=${dimension}`),
      api.get(`/api/v1/volumes/${selected}?dimension=all_time`),
      api.get(`/api/v1/customers/${selected}`),
      api.get(`/api/v1/sessions/${selected}?dimension=${dimension}`),
    ])
      .then(([u, d, v, c, s]) => {
        setUsage(u.data); setDbSizes(d.data);
        setVolumes(v.data); setCustInfo(c.data); setSessions(s.data);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));

    // Cost fetched separately so it never blocks the rest of the page
    api.get(`/api/v1/cost/customers/${selected}?months=12`)
      .then(r => setCost(r.data))
      .catch(() => setCost(null));

    api.get(`/api/v1/cost/customers/${selected}/region-trend?months=12`)
      .then(r => setRegionTrend(r.data))
      .catch(() => setRegionTrend(null));
  }, [selected, dimension]);

  const dimLabel = dimension==='last_month'?'Last month':dimension==='last_year'?'Last 12 months':'All time';

  const handleExportCsv = () => {
    const params = selected ? `?cust_code=${encodeURIComponent(selected)}` : "";
    const url = `/api/v1/customers/export/csv${params}`;
    const filename = selected ? `${selected.toUpperCase()}_profile_export.csv` : "all_customers_export.csv";
    api.get(url, { responseType: "blob" })
      .then(response => {
        const blob = new Blob([response.data], { type: "text/csv" });
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(e => setError(`CSV export failed: ${e.message}`));
  };

  const handleExportAllCsv = () => {
    const url = `/api/v1/customers/export/all-csv?dimension=${allExportDim}`;
    const dimLabel = { last_month: 'Last_Month', last_year: 'Last_12_Months', all_time: 'All_Time' }[allExportDim];
    const filename = `All_Customers_Sample_Test_Counts_${dimLabel}.csv`;
    api.get(url, { responseType: 'blob' })
      .then(response => {
        const blob = new Blob([response.data], { type: 'text/csv' });
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(e => setError(`CSV export failed: ${e.message}`));
  };



  const handlePrint = () => {
    const content = printRef.current;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`<!DOCTYPE html><html><head>
      <title>${custInfo?.cust_name} - Customer Report</title>
      <style>
        *{box-sizing:border-box;margin:0;padding:0}
        body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;font-size:12px;color:#1a1a1a;padding:24px}
        .print-header{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:20px;padding-bottom:16px;border-bottom:2px solid #e5e5e5}
        .print-header img{height:40px;width:auto}
        .customer-name{font-size:18px;font-weight:600;color:#1a1a1a}
        .print-meta{font-size:11px;color:#666;margin-top:4px}
        .badge{display:inline-block;padding:2px 8px;border-radius:20px;font-size:11px;font-weight:500}
        .badge-active{background:#dcfce7;color:#166534}.badge-inactive{background:#f3f4f6;color:#6b7280}
        .metrics{display:grid;grid-template-columns:repeat(5,1fr);gap:10px;margin-bottom:20px}
        .metric{background:#f5f5f3;border-radius:6px;padding:10px}
        .metric-label{font-size:10px;color:#666;margin-bottom:3px}
        .metric-value{font-size:15px;font-weight:600}
        .metric-sub{font-size:9px;color:#999;margin-top:2px}
        table{width:100%;border-collapse:collapse;font-size:11px;margin-bottom:16px}
        th{padding:6px 10px;text-align:left;font-weight:500;font-size:10px;color:#666;border-bottom:1px solid #e5e5e5;background:#f9f9f8}
        td{padding:6px 10px;border-bottom:1px solid #f0f0ee}
        .section-title{font-size:12px;font-weight:600;margin:16px 0 8px;border-bottom:1px solid #e5e5e5;padding-bottom:4px}
        .footer{margin-top:24px;font-size:10px;color:#999;border-top:1px solid #e5e5e5;padding-top:8px;display:flex;justify-content:space-between}
        @media print{button{display:none}body{padding:16px}}
      </style></head><body>
      <div class="print-header">
        <div>
          <div class="customer-name">${custInfo?.cust_name}</div>
          <div class="print-meta">${custInfo?.cust_code} &nbsp;·&nbsp; ${custInfo?.cl_number} &nbsp;·&nbsp; ${custInfo?.location} &nbsp;·&nbsp; Period: ${dimLabel}</div>
        </div>
        <img src="${STARLIMS_LOGO}" alt="StarLIMS" />
      </div>
      ${content.innerHTML}
      <div class="footer">
        <span>StarLIMS Cloud Usage Report</span>
        <span>Generated ${new Date().toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'})}</span>
      </div>
      </body></html>`);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 800);
  };

  const usageChart   = (usage?.rows || []).map(r => ({
    month: new Date(r.log_month).toLocaleDateString('en-GB',{month:'short',year:'2-digit'}),
    orders: r.order_count||0, tasks: r.ord_task_count||0,
  }));
  const dbChart      = (dbSizes?.months || []).map(m => ({
    month: new Date(m.report_date).toLocaleDateString('en-GB',{month:'short',year:'2-digit'}),
    total_gb: m.total_gb,
  }));
  const sessionTypes = sessions ? [...new Set(sessions.months.flatMap(m => Object.keys(m.by_type)))] : [];
  const sessionChart = (sessions?.months || []).map(m => {
    const e = { month: new Date(m.log_month).toLocaleDateString('en-GB',{month:'short',year:'2-digit'}), total: m.total };
    Object.entries(m.by_type).forEach(([k,v]) => { e[k]=v; });
    return e;
  });
  const volRegions   = volumes ? [...new Set((volumes.snapshots||[]).flatMap(s=>s.by_region.map(r=>r.region)))] : [];
  const volChart     = (volumes?.snapshots||[]).map(s => {
    const e = { date: new Date(s.snapshot_date).toLocaleDateString('en-GB',{month:'short',year:'2-digit'}) };
    s.by_region.forEach(r => { e[r.region]=r.size_gb; });
    return e;
  });
  const latestVolSnap = volumes?.snapshots?.[volumes.snapshots.length-1];
  const latestDbMonth = dbSizes?.months?.[dbSizes.months.length-1];
  const latestCost    = cost?.trend?.[cost.trend.length-1];
  const costChart     = (cost?.trend || []).map(m => ({
    month:      new Date(m.month).toLocaleDateString('en-GB',{month:'short',year:'2-digit'}),
    cost_net:   m.cost_net,
    saving:     m.cost_total_saving,
  }));

  return (
    <>
      <div style={{display:'flex',gap:12,marginBottom:20,alignItems:'center'}}>
        <select
          style={{fontSize:13,padding:'7px 12px',border:'1px solid #e5e5e5',borderRadius:6,minWidth:300}}
          value={selected} onChange={e=>setSelected(e.target.value)}
        >
          <option value="">Select a customer...</option>
          {[...customers].sort((a,b)=>a.cust_name.localeCompare(b.cust_name)).map(c=>(
            <option key={c.cust_code} value={c.cust_code}>{c.cust_name}</option>
          ))}
        </select>
        {custInfo && !loading && (
          <>
            <button onClick={handlePrint} style={{padding:'7px 16px',fontSize:13,borderRadius:6,border:'1px solid #e5e5e5',background:'#fff',cursor:'pointer'}}>
              ⎙ Print PDF
            </button>
            <button onClick={handleExportCsv} style={{padding:'7px 16px',fontSize:13,borderRadius:6,border:'1px solid #e5e5e5',background:'#fff',cursor:'pointer'}}>
              ↓ Export Sample Test Counts
            </button>
          </>
        )}
        <div style={{display:'flex',gap:8,alignItems:'center',marginLeft:'auto'}}>
          <select
            value={allExportDim}
            onChange={e => setAllExportDim(e.target.value)}
            style={{fontSize:13,padding:'7px 10px',border:'1px solid #e5e5e5',borderRadius:6,background:'#fff'}}
          >
            <option value="last_month">Last month</option>
            <option value="last_year">Last 12 months</option>
            <option value="all_time">All time</option>
          </select>
          <button
            onClick={handleExportAllCsv}
            style={{padding:'7px 16px',fontSize:13,borderRadius:6,border:'1px solid #378ADD',background:'#EBF4FF',color:'#378ADD',cursor:'pointer',whiteSpace:'nowrap'}}
          >
            ↓ Export All Customers
          </button>
        </div>
      </div>

      {!selected && <div className="loading">Select a customer to view their full profile</div>}
      {loading   && <div className="loading">Loading...</div>}
      {error     && <div className="error">Error: {error}</div>}

      {custInfo && !loading && (
        <div ref={printRef}>
          <div className="card" style={{marginBottom:16}}>
            <div className="card-body">
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                <div>
                  <div style={{fontSize:20,fontWeight:600}}>{custInfo.cust_name}</div>
                  <div style={{fontSize:13,color:'#666',marginTop:4}}>
                    {custInfo.cust_code} &nbsp;·&nbsp; {custInfo.cl_number} &nbsp;·&nbsp; {custInfo.location} &nbsp;·&nbsp; {dimLabel}
                  </div>
                </div>
                <div style={{display:'flex',alignItems:'center',gap:16}}>
                  <span className={`badge ${custInfo.is_active?'badge-active':'badge-inactive'}`}>
                    {custInfo.is_active?'Active':'Inactive'}
                  </span>
                  <img src={STARLIMS_LOGO} alt="StarLIMS" style={{height:30,width:'auto'}} />
                </div>
              </div>
            </div>
          </div>

          <div className="metrics" style={{gridTemplateColumns:'repeat(7,minmax(0,1fr))',marginBottom:20}}>
            <div className="metric"><div className="metric-label">Total samples</div><div className="metric-value">{(usage?.total_orders||0).toLocaleString()}</div><div className="metric-sub">{dimLabel}</div></div>
            <div className="metric"><div className="metric-label">Total tests</div><div className="metric-value">{(usage?.total_tasks||0).toLocaleString()}</div></div>
            <div className="metric"><div className="metric-label">Total sessions</div><div className="metric-value">{(sessions?.total||0).toLocaleString()}</div></div>
            <div className="metric"><div className="metric-label">DB size</div><div className="metric-value">{latestDbMonth?`${latestDbMonth.total_gb.toLocaleString()} GB`:'—'}</div><div className="metric-sub">{latestDbMonth?.report_date}</div></div>
            <div className="metric"><div className="metric-label">Allocated storage</div><div className="metric-value">{latestVolSnap?`${latestVolSnap.total_gb.toLocaleString()} GB`:'—'}</div><div className="metric-sub">{latestVolSnap?`${latestVolSnap.volumes?.length} volumes`:'no volumes'}</div></div>
            <div className="metric"><div className="metric-label">Net cost (mo)</div><div className="metric-value" style={{color:'#378ADD'}}>{latestCost?`$${Number(latestCost.cost_net).toLocaleString('en-US',{minimumFractionDigits:0,maximumFractionDigits:0})}`:'—'}</div><div className="metric-sub">after SP + EDP</div></div>
            <div className="metric"><div className="metric-label">Total saved (mo)</div><div className="metric-value" style={{color:'#1D9E75'}}>{latestCost?`$${Number(latestCost.cost_total_saving).toLocaleString('en-US',{minimumFractionDigits:0,maximumFractionDigits:0})}`:'—'}</div><div className="metric-sub">{latestCost&&latestCost.cost_gross>0?`${Math.round(latestCost.cost_total_saving/latestCost.cost_gross*100)}% saving`:''}</div></div>
          </div>

          <div className="two-col">
            <div className="card">
              <div className="card-header">
                <div className="card-title">Samples &amp; tests</div>
                <div className="legend">
                  <span><span className="legend-dot" style={{background:'#378ADD'}}></span>Samples</span>
                  <span><span className="legend-dot" style={{background:'#1D9E75'}}></span>Tests</span>
                </div>
              </div>
              <div className="card-body">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={usageChart} margin={{top:4,right:4,left:0,bottom:4}}>
                    <XAxis dataKey="month" tick={{fontSize:10}}/>
                    <YAxis tick={{fontSize:10}} tickFormatter={v=>v>=1000?`${(v/1000).toFixed(0)}k`:v}/>
                    <Tooltip formatter={v=>v.toLocaleString()}/>
                    <Bar dataKey="orders" name="Samples" fill="#378ADD" radius={[2,2,0,0]}/>
                    <Bar dataKey="tasks"  name="Tests"   fill="#1D9E75" radius={[2,2,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <div className="card-title">Sessions by type</div>
                <div className="legend">
                  {sessionTypes.map(t=>(
                    <span key={t}><span className="legend-dot" style={{background:TYPE_COLORS[t]||'#888'}}></span>{TYPE_LABELS[t]||t}</span>
                  ))}
                </div>
              </div>
              <div className="card-body">
                {sessionChart.length>0?(
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={sessionChart} margin={{top:4,right:4,left:0,bottom:4}}>
                      <XAxis dataKey="month" tick={{fontSize:10}}/>
                      <YAxis tick={{fontSize:10}} tickFormatter={v=>v>=1000?`${(v/1000).toFixed(0)}k`:v}/>
                      <Tooltip formatter={(v,name)=>[v.toLocaleString(),TYPE_LABELS[name]||name]}/>
                      {sessionTypes.map((t,i)=>(
                        <Bar key={t} dataKey={t} stackId="a" fill={TYPE_COLORS[t]||'#888'} radius={i===sessionTypes.length-1?[2,2,0,0]:[0,0,0,0]}/>
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                ):<div className="loading">No session data</div>}
              </div>
            </div>
          </div>

          <div className="two-col">
            <div className="card">
              <div className="card-header"><div className="card-title">Database size (GB)</div></div>
              <div className="card-body">
                {dbChart.length>0?(
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={dbChart} margin={{top:4,right:4,left:0,bottom:4}}>
                      <XAxis dataKey="month" tick={{fontSize:10}}/>
                      <YAxis tick={{fontSize:10}} tickFormatter={v=>`${v}GB`}/>
                      <Tooltip formatter={v=>`${v} GB`}/>
                      <Bar dataKey="total_gb" name="DB size" fill="#D85A30" radius={[2,2,0,0]}/>
                    </BarChart>
                  </ResponsiveContainer>
                ):<div className="loading">No DB size data</div>}
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <div className="card-title">Volume allocation (GB)</div>
                <div className="legend">
                  {volRegions.map(r=>(
                    <span key={r}><span className="legend-dot" style={{background:REGION_COLORS[r]||'#888'}}></span>{REGION_LABELS[r]||r}</span>
                  ))}
                </div>
              </div>
              <div className="card-body">
                {volChart.length>0?(
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={volChart} margin={{top:4,right:4,left:0,bottom:4}}>
                      <XAxis dataKey="date" tick={{fontSize:10}}/>
                      <YAxis tick={{fontSize:10}} tickFormatter={v=>`${v}GB`}/>
                      <Tooltip formatter={(v,name)=>[`${v} GB`,REGION_LABELS[name]||name]}/>
                      {volRegions.map((r,i)=>(
                        <Bar key={r} dataKey={r} stackId="a" fill={REGION_COLORS[r]||'#888'} radius={i===volRegions.length-1?[2,2,0,0]:[0,0,0,0]}/>
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                ):<div className="loading">No volume data</div>}
              </div>
            </div>
          </div>

          {latestVolSnap&&latestVolSnap.volumes?.length>0&&(
            <div className="card">
              <div className="card-header"><div className="card-title">E: drive volumes — {latestVolSnap.snapshot_date}</div></div>
              <table>
                <thead><tr><th>Name</th><th>Region</th><th>Size (GB)</th><th>State</th></tr></thead>
                <tbody>
                  {latestVolSnap.volumes.sort((a,b)=>b.size_gb-a.size_gb).map((v,i)=>(
                    <tr key={i}>
                      <td style={{fontWeight:500}}>{v.volume_name||'—'}</td>
                      <td style={{color:'#666'}}>{REGION_LABELS[v.region]||v.region}</td>
                      <td>{v.size_gb} GB</td>
                      <td><span className={`badge ${v.state==='in-use'?'badge-active':'badge-inactive'}`}>{v.state}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {latestDbMonth&&latestDbMonth.instances?.length>0&&(
            <div className="card">
              <div className="card-header"><div className="card-title">Database instances — {latestDbMonth.report_date}</div></div>
              <table>
                <thead><tr><th>Instance</th><th>Region</th><th>Log (MB)</th><th>Row (MB)</th><th>Total (GB)</th></tr></thead>
                <tbody>
                  {latestDbMonth.instances.sort((a,b)=>b.totalsize_gb-a.totalsize_gb).map((inst,i)=>(
                    <tr key={i}>
                      <td style={{fontWeight:500}}>{inst.db_instance}</td>
                      <td style={{color:'#666'}}>{inst.region}</td>
                      <td>{inst.logsize_mb?.toLocaleString()}</td>
                      <td>{inst.rowsize_mb?.toLocaleString()}</td>
                      <td style={{fontWeight:500}}>{inst.totalsize_gb} GB</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {sessions&&sessions.months.length>0&&(
            <div className="card">
              <div className="card-header"><div className="card-title">Session summary by month</div></div>
              <table>
                <thead>
                  <tr>
                    <th>Month</th><th>Total</th>
                    {sessionTypes.map(t=><th key={t}>{TYPE_LABELS[t]||t}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {[...sessions.months].reverse().map((m,i)=>(
                    <tr key={i}>
                      <td>{new Date(m.log_month).toLocaleDateString('en-GB',{month:'long',year:'numeric'})}</td>
                      <td style={{fontWeight:500}}>{m.total.toLocaleString()}</td>
                      {sessionTypes.map(t=>(
                        <td key={t} style={{color:'#666'}}>{(m.by_type[t]||0).toLocaleString()}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {costChart.length>0&&(
            <div className="two-col">
              <div className="card">
                <div className="card-header">
                  <div className="card-title">Monthly cost (12 months)</div>
                  <div className="legend">
                    <span><span className="legend-dot" style={{background:'#378ADD'}}></span>Net</span>
                    <span><span className="legend-dot" style={{background:'#1D9E75'}}></span>Savings</span>
                  </div>
                </div>
                <div className="card-body">
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={costChart} margin={{top:4,right:4,left:0,bottom:4}}>
                      <XAxis dataKey="month" tick={{fontSize:10}}/>
                      <YAxis tick={{fontSize:10}} tickFormatter={v=>`$${v>=1000?`${(v/1000).toFixed(0)}k`:v}`}/>
                      <Tooltip formatter={(v,name)=>[`$${Number(v).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}`,name]}/>
                      <Bar dataKey="cost_net" name="Net cost"  fill="#378ADD" stackId="a" radius={[0,0,0,0]}/>
                      <Bar dataKey="saving"   name="Savings"   fill="#1D9E75" stackId="a" radius={[2,2,0,0]}/>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <RegionTrendTable data={regionTrend} />
            </div>
          )}

        </div>
      )}
    </>
  );
}